/**
 * Hierarchical pathfinder for the strategic map (v0M2.7 spike graduated
 * to production at v0M3.1).
 *
 * Implements a "poor man's HPA*":
 *   1. Coarse layer — partition the map into 16×16-cell regions
 *      (MAP.md §2). Build a region graph where nodes = regions and
 *      edges = 4-adjacent regions with the cheapest border-cell
 *      portal between them. Run A* over regions to get a corridor.
 *   2. Fine layer — A* over cells restricted to the corridor union.
 *      8-directional with corner-cut guard, terrain-cost weighted.
 *   3. Smoothing — LOS waypoint reduction.
 *
 * Terrain costs per MAP.md §1.1 (with Infinity for impassable):
 *   Plains 1.0 · Farmland 1.0 · Forest 1.6 · Hills 1.4 · Mountains ∞
 *   Desert 1.15 · Swamp 1.9 · Lake ∞ · Sea ∞
 *
 * Wilderness impassability: wilderness is a POLITICAL concept per
 * v0M2.6g; the pathfinder is TERRAIN-driven. A wilderness cell that
 * happens to be Plains is walkable at Plains cost; a wilderness cell
 * that happens to be Mountains is impassable.
 *
 * Perf reference (Node v24 on Windows, 480×280 map): Short p95 1.94 ms
 * / Medium 2.97 / Long 3.25 / Cross-map 19.49; graph build 15.61 ms once
 * per campaign. Full report at `docs/SPIKES/v0M2.7-pathfind-perf.md`.
 */

import type { TerrainGrid } from './map-data';

// ---------------------------------------------------------------------------
// Terrain costs
// ---------------------------------------------------------------------------

/** Region side length in cells per MAP.md §2. */
export const REGION_SIZE = 16;

/** Terrain movement cost per MAP.md §1.1. Infinity = impassable. */
const TERRAIN_COST: Readonly<Record<number, number>> = {
  0: 1.0,        // Plains
  1: 1.0,        // Farmland
  2: 1.6,        // Forest
  3: 1.4,        // Hills
  4: Infinity,   // Mountains
  5: 1.15,       // Desert
  6: 1.9,        // Swamp
  7: Infinity,   // Lake
  8: Infinity,   // Sea
  // v0M7.4c (2026-07-25) — River terrain type per MAP.md §1.1
  // amendment. Impassable to armies unless a Bridge WorldStructure
  // sits on the same cell (`bridgeCells` overlay parameter — see
  // `terrainCostWithBridges` / `isPassableWithBridges`). Map
  // generation + scenario data emitting River cells is a follow-up
  // MFix; today no strategic map has River cells so this row has
  // zero live effect, but the machinery is wired so bridges just
  // work when River terrain lands.
  9: Infinity,   // River
};

export function terrainCost(id: number): number {
  const c = TERRAIN_COST[id];
  return c ?? 1.0;
}

/**
 * v0M7.4c — bridge-aware cost lookup. If `bridgeCells` includes the
 * cell (encoded as `y * width + x`), impassable cells are treated as
 * passable with `bridgeMultiplier` cost (dial `world_bridge_move_multiplier`,
 * default 1.0 = normal land). Non-impassable cells return unchanged
 * terrain cost (bridges only override impassable terrain).
 *
 * `bridgeCells === undefined` behaves identically to `terrainCost`.
 */
export function terrainCostWithBridges(
  map: TerrainGrid,
  x: number,
  y: number,
  bridgeCells: ReadonlySet<number> | undefined,
  bridgeMultiplier: number,
): number {
  const id = map.terrain[y * map.width + x]!;
  const base = terrainCost(id);
  if (Number.isFinite(base)) return base;
  if (bridgeCells === undefined) return Infinity;
  const idx = y * map.width + x;
  if (!bridgeCells.has(idx)) return Infinity;
  return bridgeMultiplier;
}

function isPassable(map: TerrainGrid, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return false;
  return Number.isFinite(terrainCost(map.terrain[y * map.width + x]!));
}

/** v0M7.4c — bridge-aware passability. Same bounds/finite check as
 *  `isPassable`, but Bridges on impassable cells count as passable. */
function isPassableWithBridges(
  map: TerrainGrid,
  x: number,
  y: number,
  bridgeCells: ReadonlySet<number> | undefined,
  bridgeMultiplier: number,
): boolean {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return false;
  return Number.isFinite(terrainCostWithBridges(map, x, y, bridgeCells, bridgeMultiplier));
}

// ---------------------------------------------------------------------------
// Priority queue (binary min-heap keyed by f-score)
// ---------------------------------------------------------------------------

interface HeapEntry {
  readonly key: number;
  readonly value: number;
}

class MinHeap {
  private readonly data: HeapEntry[] = [];

  push(key: number, value: number): void {
    this.data.push({ key, value });
    this.siftUp(this.data.length - 1);
  }

  popValue(): number {
    const top = this.data[0]!;
    const last = this.data.pop()!;
    if (this.data.length > 0) {
      this.data[0] = last;
      this.siftDown(0);
    }
    return top.value;
  }

  get size(): number {
    return this.data.length;
  }

  private siftUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.data[parent]!.key > this.data[i]!.key) {
        [this.data[parent], this.data[i]] = [this.data[i]!, this.data[parent]!];
        i = parent;
      } else {
        break;
      }
    }
  }

  private siftDown(i: number): void {
    const n = this.data.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let m = i;
      if (l < n && this.data[l]!.key < this.data[m]!.key) m = l;
      if (r < n && this.data[r]!.key < this.data[m]!.key) m = r;
      if (m === i) break;
      [this.data[m], this.data[i]] = [this.data[i]!, this.data[m]!];
      i = m;
    }
  }
}

// ---------------------------------------------------------------------------
// Region graph
// ---------------------------------------------------------------------------

export interface RegionGraph {
  readonly regionsWide: number;
  readonly regionsTall: number;
  readonly regionCount: number;
  /**
   * For each region index, the 4-adjacent neighbouring region indices with
   * their portal cost (approximate cost of crossing between region centres
   * via the cheapest border cell). Impassable neighbours are omitted.
   */
  readonly adj: ReadonlyArray<ReadonlyArray<{ readonly to: number; readonly cost: number }>>;
  /** For each region, the (cx, cy) centre cell (nearest passable, if centre is blocked). */
  readonly centres: ReadonlyArray<readonly [number, number]>;
}

function regionOfCell(map: TerrainGrid, x: number, y: number): number {
  const regionsWide = Math.ceil(map.width / REGION_SIZE);
  const rx = Math.floor(x / REGION_SIZE);
  const ry = Math.floor(y / REGION_SIZE);
  return ry * regionsWide + rx;
}

/** Cost of a step from (x0,y0) to a neighbour (x1,y1) using average terrain.
 *  v0M7.4c: bridgeCells + bridgeMultiplier let impassable-but-bridged cells
 *  contribute a finite cost. */
function stepCost(
  map: TerrainGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  bridgeCells?: ReadonlySet<number>,
  bridgeMultiplier: number = 1.0,
): number {
  const c0 = terrainCostWithBridges(map, x0, y0, bridgeCells, bridgeMultiplier);
  const c1 = terrainCostWithBridges(map, x1, y1, bridgeCells, bridgeMultiplier);
  if (!Number.isFinite(c0) || !Number.isFinite(c1)) return Infinity;
  const diag = x0 !== x1 && y0 !== y1;
  const dist = diag ? Math.SQRT2 : 1;
  return ((c0 + c1) / 2) * dist;
}

export function buildRegionGraph(map: TerrainGrid): RegionGraph {
  const regionsWide = Math.ceil(map.width / REGION_SIZE);
  const regionsTall = Math.ceil(map.height / REGION_SIZE);
  const regionCount = regionsWide * regionsTall;

  // Pick a passable centre per region (nearest to the geometric centre).
  const centres: [number, number][] = [];
  for (let ry = 0; ry < regionsTall; ry++) {
    for (let rx = 0; rx < regionsWide; rx++) {
      const cx0 = rx * REGION_SIZE + Math.floor(REGION_SIZE / 2);
      const cy0 = ry * REGION_SIZE + Math.floor(REGION_SIZE / 2);
      let found: [number, number] | null = null;
      // Spiral out from the geometric centre until we find a passable cell
      // or exhaust the region.
      for (let r = 0; r <= REGION_SIZE && !found; r++) {
        for (let dy = -r; dy <= r && !found; dy++) {
          for (let dx = -r; dx <= r && !found; dx++) {
            const x = cx0 + dx;
            const y = cy0 + dy;
            if (x < 0 || y < 0 || x >= map.width || y >= map.height) continue;
            if (Math.floor(x / REGION_SIZE) !== rx) continue;
            if (Math.floor(y / REGION_SIZE) !== ry) continue;
            if (isPassable(map, x, y)) {
              found = [x, y];
            }
          }
        }
      }
      // If no passable cell exists in this region, use the geometric centre;
      // the region will be flagged unreachable via infinite edge costs.
      centres.push(found ?? [Math.min(cx0, map.width - 1), Math.min(cy0, map.height - 1)]);
    }
  }

  // Build 4-adjacency with a portal cost approximated by the cheapest
  // border cell between the two regions' shared edge.
  const adj: { to: number; cost: number }[][] = [];
  for (let ry = 0; ry < regionsTall; ry++) {
    for (let rx = 0; rx < regionsWide; rx++) {
      const neighbours: { to: number; cost: number }[] = [];

      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const nrx = rx + dx;
        const nry = ry + dy;
        if (nrx < 0 || nry < 0 || nrx >= regionsWide || nry >= regionsTall) continue;
        const there = nry * regionsWide + nrx;

        // Cheapest portal on the shared border
        let bestPortalCost = Infinity;
        if (dx === 1) {
          const bx = rx * REGION_SIZE + REGION_SIZE - 1;
          const bxN = nrx * REGION_SIZE;
          const y0 = ry * REGION_SIZE;
          const y1 = Math.min(map.height, y0 + REGION_SIZE);
          for (let y = y0; y < y1; y++) {
            if (bx >= map.width || bxN >= map.width) break;
            const c = stepCost(map, bx, y, bxN, y);
            if (c < bestPortalCost) bestPortalCost = c;
          }
        } else if (dx === -1) {
          const bx = rx * REGION_SIZE;
          const bxN = nrx * REGION_SIZE + REGION_SIZE - 1;
          const y0 = ry * REGION_SIZE;
          const y1 = Math.min(map.height, y0 + REGION_SIZE);
          for (let y = y0; y < y1; y++) {
            if (bx < 0 || bxN < 0) break;
            const c = stepCost(map, bx, y, bxN, y);
            if (c < bestPortalCost) bestPortalCost = c;
          }
        } else if (dy === 1) {
          const by = ry * REGION_SIZE + REGION_SIZE - 1;
          const byN = nry * REGION_SIZE;
          const x0 = rx * REGION_SIZE;
          const x1 = Math.min(map.width, x0 + REGION_SIZE);
          for (let x = x0; x < x1; x++) {
            if (by >= map.height || byN >= map.height) break;
            const c = stepCost(map, x, by, x, byN);
            if (c < bestPortalCost) bestPortalCost = c;
          }
        } else if (dy === -1) {
          const by = ry * REGION_SIZE;
          const byN = nry * REGION_SIZE + REGION_SIZE - 1;
          const x0 = rx * REGION_SIZE;
          const x1 = Math.min(map.width, x0 + REGION_SIZE);
          for (let x = x0; x < x1; x++) {
            if (by < 0 || byN < 0) break;
            const c = stepCost(map, x, by, x, byN);
            if (c < bestPortalCost) bestPortalCost = c;
          }
        }

        if (Number.isFinite(bestPortalCost)) {
          // Add estimated cost from region centre to portal and back —
          // approximated as region-size × portal step cost.
          const edgeCost = REGION_SIZE * bestPortalCost;
          neighbours.push({ to: there, cost: edgeCost });
        }
      }
      adj.push(neighbours);
    }
  }

  return { regionsWide, regionsTall, regionCount, adj, centres };
}

// ---------------------------------------------------------------------------
// Coarse A* — over regions
// ---------------------------------------------------------------------------

function coarseAstar(
  graph: RegionGraph,
  startRegion: number,
  goalRegion: number,
): number[] | null {
  if (startRegion === goalRegion) return [startRegion];

  const g = new Float64Array(graph.regionCount).fill(Infinity);
  const cameFrom = new Int32Array(graph.regionCount).fill(-1);
  g[startRegion] = 0;

  const [gx, gy] = graph.centres[goalRegion]!;
  const heuristic = (r: number): number => {
    const [cx, cy] = graph.centres[r]!;
    const dx = gx - cx;
    const dy = gy - cy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const open = new MinHeap();
  open.push(heuristic(startRegion), startRegion);

  while (open.size > 0) {
    const current = open.popValue();
    if (current === goalRegion) {
      const path: number[] = [];
      let c = current;
      while (c !== -1) {
        path.push(c);
        c = cameFrom[c]!;
      }
      return path.reverse();
    }
    for (const { to, cost } of graph.adj[current]!) {
      const tentativeG = g[current]! + cost;
      if (tentativeG < g[to]!) {
        g[to] = tentativeG;
        cameFrom[to] = current;
        open.push(tentativeG + heuristic(to), to);
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Fine A* — over cells restricted to the corridor
// ---------------------------------------------------------------------------

/**
 * @param corridor  A mask of allowed cells (1 = allowed, 0 = disallowed).
 *                  Pass `null` to allow the entire map (flat A*), used as
 *                  the fallback when the coarse corridor missed interior
 *                  obstructions.
 */
function fineAstar(
  map: TerrainGrid,
  corridor: Uint8Array | null,
  startX: number,
  startY: number,
  goalX: number,
  goalY: number,
  bridgeCells?: ReadonlySet<number>,
  bridgeMultiplier: number = 1.0,
): { path: [number, number][]; cost: number } | null {
  const W = map.width;
  const H = map.height;
  const size = W * H;

  const g = new Float64Array(size).fill(Infinity);
  const cameFrom = new Int32Array(size).fill(-1);
  const startIdx = startY * W + startX;
  const goalIdx = goalY * W + goalX;

  g[startIdx] = 0;

  const heuristic = (x: number, y: number): number => {
    const dx = Math.abs(goalX - x);
    const dy = Math.abs(goalY - y);
    // Octile
    return (Math.max(dx, dy) - Math.min(dx, dy)) + Math.SQRT2 * Math.min(dx, dy);
  };

  const open = new MinHeap();
  open.push(heuristic(startX, startY), startIdx);

  const NEIGHBOURS: readonly [number, number][] = [
    [1, 0], [-1, 0], [0, 1], [0, -1],
    [1, 1], [1, -1], [-1, 1], [-1, -1],
  ];

  while (open.size > 0) {
    const currentIdx = open.popValue();
    if (currentIdx === goalIdx) {
      const path: [number, number][] = [];
      let c = currentIdx;
      while (c !== -1) {
        path.push([c % W, Math.floor(c / W)]);
        c = cameFrom[c]!;
      }
      path.reverse();
      return { path, cost: g[goalIdx]! };
    }
    const cx = currentIdx % W;
    const cy = Math.floor(currentIdx / W);
    for (const [dx, dy] of NEIGHBOURS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const nIdx = ny * W + nx;
      if (corridor !== null && corridor[nIdx] === 0) continue;
      if (!isPassableWithBridges(map, nx, ny, bridgeCells, bridgeMultiplier)) continue;
      // Corner-cut guard for diagonals
      if (dx !== 0 && dy !== 0) {
        if (
          !isPassableWithBridges(map, cx + dx, cy, bridgeCells, bridgeMultiplier) ||
          !isPassableWithBridges(map, cx, cy + dy, bridgeCells, bridgeMultiplier)
        ) continue;
      }
      const step = stepCost(map, cx, cy, nx, ny, bridgeCells, bridgeMultiplier);
      if (!Number.isFinite(step)) continue;
      const tentativeG = g[currentIdx]! + step;
      if (tentativeG < g[nIdx]!) {
        g[nIdx] = tentativeG;
        cameFrom[nIdx] = currentIdx;
        open.push(tentativeG + heuristic(nx, ny), nIdx);
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// LOS smoothing
// ---------------------------------------------------------------------------

function lineOfSightClear(
  map: TerrainGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): boolean {
  // Bresenham with impassable-cell check + corner-cut prevention.
  //
  // MFix12.1 (2026-07-27) — corner-cut prevention for diagonal steps.
  // Playtest surfaced armies cutting BETWEEN two diagonally-touching river
  // cells (Michael's diagnosis after visual observation). Fine A* enforces
  // corner-cut prevention: a diagonal step from (cx,cy) to (cx+dx,cy+dy)
  // requires both orthogonal neighbours (cx+dx,cy) and (cx,cy+dy) to be
  // passable. LOS smoothing previously only checked the cells the Bresenham
  // line literally stepped THROUGH — it missed diagonal skips past river
  // cells because the specific cells the line touched were all Plains,
  // even though the two river cells sitting diagonally at each corner
  // physically block the move.
  //
  // Fix: mirror fine A*'s corner-cut rule. When the Bresenham advance
  // moves BOTH x AND y in the same iteration (diagonal step), reject the
  // move if either orthogonal neighbour is impassable. This makes smoothed
  // paths honour the same passability contract as fine A*.
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  while (true) {
    if (!isPassable(map, x, y)) return false;
    if (x === x1 && y === y1) return true;
    const e2 = 2 * err;
    let advX = 0;
    let advY = 0;
    if (e2 > -dy) {
      err -= dy;
      advX = sx;
    }
    if (e2 < dx) {
      err += dx;
      advY = sy;
    }
    // Corner-cut guard: only fires on diagonal steps (both advX and advY
    // non-zero). Requires both orthogonal neighbours passable, matching
    // the fine-A* neighbour expansion rule in this same file.
    if (advX !== 0 && advY !== 0) {
      if (!isPassable(map, x + advX, y) || !isPassable(map, x, y + advY)) {
        return false;
      }
    }
    x += advX;
    y += advY;
  }
}

function smoothPath(map: TerrainGrid, path: readonly [number, number][]): [number, number][] {
  if (path.length <= 2) return path.slice();
  const out: [number, number][] = [path[0]!];
  let i = 0;
  while (i < path.length - 1) {
    let j = path.length - 1;
    while (j > i + 1) {
      const [ax, ay] = path[i]!;
      const [bx, by] = path[j]!;
      if (lineOfSightClear(map, ax, ay, bx, by)) break;
      j--;
    }
    out.push(path[j]!);
    i = j;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface PathResult {
  readonly found: boolean;
  readonly cells: readonly [number, number][];
  readonly smoothedCells: readonly [number, number][];
  readonly totalCost: number;
  readonly corridorRegionCount: number;
  /**
   * True if the coarse corridor missed interior obstructions and the
   * pathfinder fell back to flat A* over the whole map. Tracked so the
   * perf report can distinguish clean-corridor cases from fallback cases.
   */
  readonly usedFallback: boolean;
}

export function hierarchicalPath(
  map: TerrainGrid,
  graph: RegionGraph,
  startX: number,
  startY: number,
  goalX: number,
  goalY: number,
  /**
   * v0M7.4c (2026-07-25) — optional bridge overlay per MAP.md §9. Set
   *  of cell indices (y × width + x) where a Bridge WorldStructure
   *  makes otherwise-impassable terrain (River / Lake / Sea) passable
   *  at `bridgeMultiplier` cost. Callers who don't compute bridges
   *  pass undefined; the pathfinder behaves as pre-v0M7. The coarse
   *  region graph is bridge-unaware at v0M7 — cross-river paths fall
   *  back to flat A* (correct but slower). Rebuilding regionGraph
   *  when bridges appear is a follow-up MFix if profiler flags it.
   */
  bridgeCells?: ReadonlySet<number>,
  bridgeMultiplier: number = 1.0,
): PathResult {
  const empty: PathResult = {
    found: false,
    cells: [],
    smoothedCells: [],
    totalCost: 0,
    corridorRegionCount: 0,
    usedFallback: false,
  };

  if (
    !isPassableWithBridges(map, startX, startY, bridgeCells, bridgeMultiplier) ||
    !isPassableWithBridges(map, goalX, goalY, bridgeCells, bridgeMultiplier)
  ) {
    return empty;
  }

  const startRegion = regionOfCell(map, startX, startY);
  const goalRegion = regionOfCell(map, goalX, goalY);

  // Coarse A*
  const regionPath = coarseAstar(graph, startRegion, goalRegion);

  // Build corridor mask (1 = allowed) if the coarse layer returned a route.
  // Uint8Array beats Set<number> lookup by ~10-20× at this scale.
  let corridor: Uint8Array | null = null;
  let corridorRegionCount = 0;
  if (regionPath !== null) {
    corridor = new Uint8Array(map.width * map.height);
    for (const r of regionPath) {
      const rx = r % graph.regionsWide;
      const ry = Math.floor(r / graph.regionsWide);
      const x0 = rx * REGION_SIZE;
      const y0 = ry * REGION_SIZE;
      const x1 = Math.min(map.width, x0 + REGION_SIZE);
      const y1 = Math.min(map.height, y0 + REGION_SIZE);
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          corridor[y * map.width + x] = 1;
        }
      }
    }
    corridorRegionCount = regionPath.length;
  }

  // Fine A* on the coarse corridor.
  let fine = corridor !== null
    ? fineAstar(map, corridor, startX, startY, goalX, goalY, bridgeCells, bridgeMultiplier)
    : null;

  // Fallback: coarse layer only checks border portals, so interior
  // obstructions (e.g. a mountain wall wholly inside a region) can
  // segment the corridor. Retry with flat A* over the entire map.
  // v0M7.4c — this fallback is also the path taken for cross-river
  // routes via Bridge (the coarse regionGraph is bridge-unaware).
  let usedFallback = false;
  if (fine === null) {
    fine = fineAstar(map, null, startX, startY, goalX, goalY, bridgeCells, bridgeMultiplier);
    usedFallback = fine !== null;
  }

  if (fine === null) return empty;

  // Smooth
  const smoothed = smoothPath(map, fine.path);

  return {
    found: true,
    cells: fine.path,
    smoothedCells: smoothed,
    totalCost: fine.cost,
    corridorRegionCount,
    usedFallback,
  };
}
